// Lazy chunk (audit 2026-07-06 #17): chart.js loads as its own chunk in
// parallel with boot instead of sitting inside the critical main bundle
// (~270 kB off first paint). _safeChart calls that arrive before the library
// resolves are queued and replayed once it lands — callers keep their normal
// null-tolerant behavior (_chartUpdate falls back to _safeChart when null).
let Chart = null;
const _pendingCharts = [];
import('chart.js/auto').then(m => {
  Chart = m.default;
  _pendingCharts.splice(0).forEach(([id, config]) => _safeChart(id, config));
}).catch(e => console.error('[ZAGX] chart.js chunk failed to load:', e));

/**
 * Theme-aware chart colors — returns colors based on current light/dark mode
 */
export function getChartTheme() {
  const isLight = document.documentElement.getAttribute('data-theme') === 'light';
  return {
    text: isLight ? 'rgba(28,25,24,.5)' : 'rgba(225,215,195,.45)',
    textMuted: isLight ? 'rgba(28,25,24,.3)' : 'rgba(225,215,195,.3)',
    grid: isLight ? 'rgba(28,25,24,.06)' : 'rgba(255,255,255,.04)',
    border: isLight ? '#E0DED9' : '#2D2D2A',
    tooltipBg: isLight ? '#F0EFEC' : '#333330',
    tooltipText: isLight ? '#1C1918' : '#F2EBDD',
    tooltipBorder: isLight ? 'rgba(28,25,24,.08)' : 'rgba(255,255,255,.06)',
    investedLine: isLight ? 'rgba(28,25,24,.2)' : 'rgba(255,255,255,.3)',
  };
}

/**
 * Safe chart destroy — prevents errors on already-destroyed or null charts
 * @param {Chart} chart - Chart.js instance
 * @returns {null}
 */
export function _safeDestroy(chart) {
  try {
    if (chart && typeof chart.destroy === 'function') {
      chart.destroy();
    }
  } catch (e) {
    console.warn('[ZAGX] Chart destroy error:', e);
  }
  return null;
}

/**
 * Safe chart create — validates canvas exists before creating
 * CE-13 Fix: Destroy any orphaned Chart.js instance on this canvas before creating
 * @param {string} canvasId - Canvas element ID
 * @param {Object} config - Chart.js config object
 * @returns {Chart|null}
 */
export function _safeChart(canvasId, config) {
  const cvs = document.getElementById(canvasId);
  if (!cvs) {
    console.warn('[ZAGX] Canvas not found:', canvasId);
    return null;
  }
  // Library chunk still loading — queue and replay when it lands.
  if (!Chart) {
    _pendingCharts.push([canvasId, config]);
    return null;
  }
  // CE-13 Fix: Destroy any orphaned Chart.js instance on this canvas before creating
  try {
    const existing = Chart.getChart(cvs);
    if (existing) existing.destroy();
  } catch (_e) {}
  try {
    return new Chart(cvs, config);
  } catch (e) {
    console.error('[ZAGX] Chart create error:', canvasId, e);
    return null;
  }
}

/**
 * Chart helper: update data in-place if chart exists, else create new
 * @param {Chart} chart - Existing chart instance
 * @param {string} canvasId - Canvas element ID
 * @param {string} type - Chart type (doughnut, bar, line, etc)
 * @param {Array} labels - Chart labels
 * @param {Array} data - Chart data array
 * @param {Array} colors - Dataset colors
 * @param {string} titleText - Optional chart title
 * @param {Object} opt - Chart.js options
 * @returns {Chart|null}
 */
export function _chartUpdate(chart, canvasId, type, labels, data, colors, titleText, opt) {
  if (chart && chart.canvas && chart.canvas.id === canvasId) {
    try {
      chart.data.labels = labels;
      chart.data.datasets[0].data = data;
      chart.data.datasets[0].backgroundColor = colors;
      chart.update('none');
      return chart;
    } catch (_e) {
      chart = _safeDestroy(chart);
    }
  }
  chart = _safeDestroy(chart);
  const th = getChartTheme();
  const chartOpt = { ...opt };
  if (titleText) {
    chartOpt.plugins = {
      ...opt.plugins,
      title: {
        display: true,
        text: titleText,
        color: th.text,
        font: { size: 11 }
      }
    };
  }
  return _safeChart(canvasId, {
    type,
    data: {
      labels,
      datasets: [{
        data,
        backgroundColor: colors,
        borderColor: th.border,
        borderWidth: 2
      }]
    },
    options: chartOpt
  });
}

/**
 * Render allocation charts (by investment and by current value)
 * @param {Object} td - Type distribution object {Stock:{inv,val,n}, ETF:{inv,val,n}, ...}
 * @returns {void}
 */
export function renderAllocCh(td) {
  const tc = {
    Stock: '#7ff0c4',
    ETF: '#8b5cf6',
    Fund: '#a78bfa',
    Commodity: '#FF8C42',
    Cash: '#4DC9F6',
    Crypto: '#D4A853'
  };
  const lb = Object.keys(td);
  const inv = lb.map(l => td[l].inv);
  const val = lb.map(l => td[l].val);
  const col = lb.map(l => tc[l] || '#8b5cf6');

  const th = getChartTheme();
  const opt = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: th.text,
          font: { size: 10 }
        }
      }
    }
  };

  // Exported chart variables (ch1, ch2) are expected to be in outer scope
  // This function updates global chart references
  if (typeof ch1 !== 'undefined') {
    window.ch1 = _chartUpdate(window.ch1, 'chInv', 'doughnut', lb, inv, col, 'By Investment', opt);
  }
  if (typeof ch2 !== 'undefined') {
    if (val.some(v => v > 0)) {
      window.ch2 = _chartUpdate(window.ch2, 'chCur', 'doughnut', lb, val, col, 'By Current Value', opt);
    } else {
      window.ch2 = _safeDestroy(window.ch2);
      const cvs = document.getElementById('chCur');
      if (cvs) {
        const c = cvs.getContext('2d');
        c.clearRect(0, 0, cvs.width, cvs.height);
        c.fillStyle = '#4a5568';
        c.font = '12px sans-serif';
        c.fillText('Enter qty & prices for current allocation', 10, 100);
      }
    }
  }
}
